<template>
  <div class="m-4 md:mx-8 md:mt-16">
    <section>
      <h1 class="px-4 md:px-8 py-2 text-2xl font-bold bg-primario text-gray-200 tracking-wide rounded-t">
        {{ title }} ({{ totalRows }})
      </h1>
      <div class="bg-white p-4 md:p-8 rounded-b-sm">
        <form class="flex flex-wrap md:flex-no-wrap justify-between md:space-x-3 space-y-3 md:space-y-0">
          <div class="flex w-full md:max-w-xl flex-col space-y-3">
            <div class="grid grid-cols-2 gap-2">
              <div class="relative inline-block text-gray-500 focus-within:text-gray-700 md:max-w-xs">
                <span class="absolute inset-y-0 right-0 outline-none">
                  <svg class="w-5 h-5 mr-2 mt-1.5 fill-current outline-none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.32 14.9l5.39 5.4a1 1 0 0 1-1.42 1.4l-5.38-5.38a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
                  </svg>
                </span>
                <input
                  v-model="filters.searchReference"
                  type="text"
                  :placeholder="$t('lang.msg.searchByReference')"
                  class="form-input w-full h-8 pr-8 appearance-none border border-gray-500 rounded py-2 pl-4 text-gray-900 placeholder-gray-500 leading-5 focus:outline-none focus:shadow-outline"
                >
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div v-if="hasRoles(['CLIENTE'])" />
              <Select
                v-if="!hasRoles(['CLIENTE'])"
                id="idClient"
                :data="clients"
                class="md:max-w-xs"
                track-by="clientName"
                :placeholder="$t('lang.msg.searchByClient')"
                v-model.number="filters.idClient"
                @input="changeFilter"
              />
              <Select
                id="idFactory"
                :data="factories"
                class="max-w-xs"
                track-by="factoryName"
                :placeholder="$t('lang.msg.searchByFactory')"
                v-model.number="filters.idFactory"
                @input="changeFilter"
              />
            </div>
            <div class="grid grid-cols-2 gap-2">
              <Select
                id="idCollection"
                :data="collections"
                class="md:max-w-xs"
                :track-by="getLangLabel('collection')"
                :placeholder="$t('lang.msg.searchByCollection')"
                v-model.number="filters.idCollection"
                @input="changeFilter"
              />
              <Select
                id="idFactoryAgent"
                :data="agents"
                class="max-w-xs"
                track-by="factoryAgentName"
                :placeholder="$t('lang.msg.searchByAgent')"
                v-model.number="filters.idFactoryAgent"
                @input="changeFilter"
              />
            </div>
          </div>
          <div class="flex flex-col justify-end w-full md:max-w-72 space-y-3">
            <ButtonRedirect route="planningSample" v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])">
              {{ $t("lang.msg.addProduction") }}
            </ButtonRedirect>
            <!-- === FILTROS === -->
            <div class="flex space-x-2">
              <ButtonAction large color="verde" @action="filterAction">
                {{ $t("lang.msg.filter") }}
              </ButtonAction>
              <ButtonAction large color="red" @action="removeFilters(`api/Productions/Pagination`, `api/Productions/TotalRegistros`)">
                {{ $t("lang.msg.removeFilters") }}
              </ButtonAction>
            </div>
            <!-- ================================ -->
            <!-- === ACTIVOS / DESCATALOGADOS === -->
            <!-- ================================ -->
            <ButtonAction
              v-if="showingActivePlannings"
              v-show="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
              large
              color="lightgray"
              @action="setUpDiscontinued"
            >
              {{ $t("lang.msg.viewDiscontinued") }}
            </ButtonAction>
            <ButtonAction
              v-else
              large
              color="lightgray"
              @action="removeFilters(`api/Productions/Pagination`, `api/Productions/TotalRegistros`)"
            >
              {{ $t("lang.msg.viewActivePlannings") }}
            </ButtonAction>
          </div>
        </form>

        <PlanningPagination
          :current-page="page"
          :total-rows="totalRows"
          :visible-rows="visibleRows"
          @next-page="fetchPaginationData('next');"
          @previous-page="fetchPaginationData('previous');"
          @load-selected-page="fetchPaginationData"
          @take-rows="takeRows"
        />

        <!-- === PLANNING TABLE === -->
        <ul id="PlanningTable">
            <draggable
                v-model="planning"
                :disabled="isSmallScreen">

            <li
            v-for="(item, index) in planning"
            :key="`item_${index}`"
            class="mt-6 xl:pr-2 grid items-center grid-cols-1 sm:grid-cols-2 xl:grid-cols-production gap-0 sm:gap-4"
            :class="{'bg-gray-200':index % 2 == 0,'border-red':item.descatalogada}"
          >
            <!-- ======================= -->
            <!-- === PLANNING IMAGES === -->
            <!-- ======================= -->
              <PlanningImages
                  :id-comercial-request="item.idComercialRequest"
                  :join-life="item.joinLife"
                  :reoperate="item.productionReoperation"
                  :official-delivery-date1="item.officialDeliveryDate1"
                  :reference-number="item.comercialRequestReferenceNumber"
                  :discontinued="item.descatalogada"
                  :cloned="item.cloned"
              />
            <!-- ====================================-->
            <!-- === PLANNING PRODUCTION PROGRESS === -->
            <!-- ==================================== -->
            <PlanningProductionProgress :production="item" :update="onPageTransit" />
            <div class="xl:space-y-2 xl:my-2 cursor-pointer" @click="viewPlanning(index)">
              <PlanningTableItem :index="index" :data="item.comercialRequestReference" :label="$t('lang.msg.reference')" />
<!--              <PlanningTableItem :index="index" :data="item.comercialRequestReference2" :label="$t('lang.msg.reference2')" />-->
              <PlanningTableItem :index="index" :data="item.clientName" :label="$t('lang.msg.client')" />
              <PlanningTableItem :index="index" :data="item.sectionName" :label="$t('lang.msg.department')" />
              <PlanningTableItem :index="index" :data="item.precioCompra" :label="$t('lang.msg.purchasePrice')" />
            </div>
            <div class="xl:space-y-2 xl:my-2 cursor-pointer" @click="viewPlanning(index)">
              <PlanningTableItem :index="index" :data="item.salePrice2" :label="$t('lang.msg.salePrice')" />
              <PlanningTableItem :index="index" :data="item.totalQuantity" :label="$t('lang.msg.units')" />
              <PlanningTableItem :index="index" :data="item.factoryName" :label="$t('lang.msg.factory')" />
              <PlanningTableItem :index="index" :data="item.officialDeliveryDate1|moment" :label="$t('lang.msg.officialDate1')" />
            </div>
            <div class="xl:my-2 cursor-pointer">
              <div class="flex justify-end items-center xl:flex-col space-x-4 xl:space-x-0 xl:space-y-3">
                  <ButtonRounded
                      title="Mover"
                      color=""
                      large
                      v-if="!isSmallScreen"
                  >
                      <svg class="w-6 h-6 fill-current" viewBox="0 0 28 28" stroke="currentColor">
                          <path d="M28 14c0 0.266-0.109 0.516-0.297 0.703l-4 4c-0.187 0.187-0.438 0.297-0.703 0.297-0.547 0-1-0.453-1-1v-2h-6v6h2c0.547 0 1 0.453 1 1 0 0.266-0.109 0.516-0.297 0.703l-4 4c-0.187 0.187-0.438 0.297-0.703 0.297s-0.516-0.109-0.703-0.297l-4-4c-0.187-0.187-0.297-0.438-0.297-0.703 0-0.547 0.453-1 1-1h2v-6h-6v2c0 0.547-0.453 1-1 1-0.266 0-0.516-0.109-0.703-0.297l-4-4c-0.187-0.187-0.297-0.438-0.297-0.703s0.109-0.516 0.297-0.703l4-4c0.187-0.187 0.438-0.297 0.703-0.297 0.547 0 1 0.453 1 1v2h6v-6h-2c-0.547 0-1-0.453-1-1 0-0.266 0.109-0.516 0.297-0.703l4-4c0.187-0.187 0.438-0.297 0.703-0.297s0.516 0.109 0.703 0.297l4 4c0.187 0.187 0.297 0.438 0.297 0.703 0 0.547-0.453 1-1 1h-2v6h6v-2c0-0.547 0.453-1 1-1 0.266 0 0.516 0.109 0.703 0.297l4 4c0.187 0.187 0.297 0.438 0.297 0.703z"></path>
                      </svg>
                  </ButtonRounded>
                <ButtonRounded
                  @action="openNotificationsModal(item)"
                  title="Ver notas ext."
                  color="blue"
                  large
                >
                  <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 10H8.01M12 10H12.01M16 10H16.01M9 16H5C3.89543 16 3 15.1046 3 14V6C3 4.89543 3.89543 4 5 4H19C20.1046 4 21 4.89543 21 6V14C21 15.1046 20.1046 16 19 16H14L9 21V16Z" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </ButtonRounded>
                <ButtonRounded
                  @action="viewPlanning(index)"
                  title="Ver Producción"
                  color="blue"
                  large
                >
                  <svg class="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.5 283.5">
                    <path d="M275.2 50.3c-3.5-4.5-7.7-8.3-12.7-11.2-7.3-4.4-15.1-7.5-23.7-8.3-5.1-.5-10.2-.5-15.2.8-4.8 1.3-9 3.5-12.6 7-3.3 3.2-4.8 7.1-5.4 11.6-.1.9-.2 1.9-.4 2.8-.2 1.3-.5 1.6-1.9 1.7-2 .1-4 .1-6.1-.1-4.7-.3-9.3-.8-14-.7-1.6 0-3.2.1-4.7-.1-4.7-.5-9.3-.4-14-.6-5.8-.2-11.6-.1-17.4-.1-7.4 0-14.8-.1-22.1.2-5.7.2-11.5.7-17.2.5-.9 0-1.8.1-2.6.1-4.1.3-8.2.7-12.3.6-.9 0-1.9 0-2.8.1-4.5.4-8.9.7-13.4.7-.8 0-1.5 0-2.3.1-4.9.6-9.9.7-14.9 1.3-4 .5-8 1-11.9 1.9-5.5 1.2-10.8 3-15.9 5.3-9.1 4.1-16.1 10.5-20.9 19.3-4.7 8.8-6.4 18.4-6.5 28.3 0 1.4.3 2.9.3 4.3 0 2.7.5 5.4 1 8.1 1.3 6.6 3.5 13 6.5 19.1 3 6.1 6.5 11.8 10.7 17.1 3.6 4.5 7.5 8.8 11.9 12.7 7.2 6.4 15.1 11.6 23.9 15.4 6 2.6 12.2 4.8 18.6 6.2 3 .6 6 1.3 9.1 1.7 4.7.7 9.5.9 14.3 1.2 4.7.3 9.3-.1 14-.5 4.6-.3 9.3-1.1 13.8-2 5.7-1.1 11.2-2.6 16.6-4.6 10.8-4.1 20.5-10.1 28.3-18.7 4.2-4.6 7.4-9.8 8.7-16.1 1.2-5.9.5-11.5-2.1-16.9-1.9-4-4.5-7.6-7.9-10.6-2.8-2.4-5.9-4.3-9.3-5.7-7.8-3.2-16-3.4-24.2-2.8-5.9.4-11.7 2-17.2 4.1-9.1 3.5-17.3 8.6-23.9 15.9-3.1 3.5-5.6 7.3-7.2 11.7-.8 2.1-1.3 4.3-1.6 6.6-.2 1.8-1.2 2.8-2.9 3.3-2.6.7-5.1 1.3-7.8 1.6-6.5.8-12.6-.5-18.4-3.1-6-2.6-11-6.5-14.8-11.7-4.6-6.2-7.3-13.1-7.3-20.9v-4.2c0-6 1.8-11.4 4.9-16.5 4.3-6.8 10.6-11.1 17.8-14.2 5.2-2.3 10.7-3.7 16.3-4.8 5-1 10-1.5 15-2.1 2.6-.3 5.3-.3 7.9-.3 5.4.1 10.7-.1 16.1.3 5 .3 10.1-.1 15.1-.2 7.7-.1 15.4-.4 23.1-.7 8.3-.3 16.7-.2 25 .4 2.3.2 4.7.1 7 .4 5.1.6 10.1 1.2 15.1 2.5 4.3 1.1 8.6 2.4 12.6 4.6 5.3 2.9 9.1 7.1 11.4 12.7 2 4.9 3.1 10.1 3.8 15.4.7 5.4 1 10.8 1 16.2 0 4-.5 7.9-.4 11.9 0 .3 0 .5-.1.8-.7 5.2-1 10.5-2 15.7-.6 2.9-1.2 5.8-2.1 8.6-1.5 4.8-3.5 9.3-6 13.7-2.3 4-5.1 7.7-8.2 11.1-7.2 7.7-16.1 12.9-25.9 16.5-2.8 1.1-5.7 1.9-8.7 2.7-3.9 1.1-7.8 1.9-11.8 2.7-4 .7-8.1 1.2-12.1 1.8-4.5.6-9.1.7-13.6.9-7.1.3-14.2-.2-21.3-.9-4.5-.5-8.9-1.1-13.3-1.9-6.6-1.2-13-2.9-19.4-5-9-2.9-17.6-6.6-25.9-11-5.9-3.2-11.6-6.8-17.1-10.8-4.7-3.5-9.8-6.5-15.5-8-4-1.1-7.3 0-10.6 2.1-.9.6-1.8 1.4-2.5 2.3-2.8 3.6-2.8 7.6-2 11.7 1 5.1 3.5 9.6 6.6 13.7 3.3 4.3 7.5 7.5 11.8 10.5 5.3 3.7 10.9 6.9 16.7 9.6 6.5 3 13.2 5.7 20.1 7.9 6.6 2.1 13.2 3.7 19.9 5.2 2 .4 3.9.8 5.9 1.2 2.7.4 5.4.4 8 1.3.5.2 1 .2 1.5.2 3.2.1 6.3.6 9.4 1 6.2.7 12.3.7 18.5.9 4.1.1 8.2.1 12.3-.3 3.2-.3 6.4.1 9.6-.5 2.3-.4 4.7-.1 7-.6 2.9-.6 6-.2 8.7-1.5.2-.1.4-.1.6-.1 2.7-.2 5.4-.6 8-1.2 7-1.6 14-3.3 20.8-5.5 8.6-2.8 16.8-6.6 24.6-11.1 8.1-4.6 15.4-10.3 21.7-17.2 2.7-3 5.3-6.1 7.5-9.5 3.4-5.2 6-10.9 7.8-17 2.3-8.2 3.5-16.5 3.9-25 .5-10.8-.2-21.6-1.6-32.3-.6-4.1-1-8.3-2-12.3l-.9-4.5c-.8-4.2-1.7-8.4-2.8-12.5-.7-2.6-1.5-5.2-2.2-7.8-.4-1.5 0-2 1.4-2.3 1-.3 2.1-.5 3.1-.8 7.4-1.9 13.6-5.8 18.4-11.9 2.3-2.9 4.1-6.2 4.7-9.8 1.4-6.9-.5-13.2-4.8-18.8zM254.8 56c-1.7 6.6-10.1 7-14.7 4 0 0-2.5-10.2.7-12.8 3.2-2.6 7.4-1.1 7.4-1.1s8.2 3.3 6.6 9.9z" />
                  </svg>
                </ButtonRounded>
                <ButtonRounded
                  v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                  @action="openDiscontinueModal(index, item.idProduction)"
                  title="Descatalogar producción"
                  color="red"
                  large
                >
                  <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      d="M6 18L18 6M6 6L18 18"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </ButtonRounded>
              </div>
            </div>
          </li>
            </draggable>
        </ul>

        <PlanningPagination
          :current-page="page"
          :total-rows="totalRows"
          :visible-rows="visibleRows"
          @next-page="fetchPaginationData('next');"
          @previous-page="fetchPaginationData('previous');"
          @load-selected-page="fetchPaginationData"
          @take-rows="takeRows"
        />
      </div>
    </section>

    <!-- === CALENDAR AND TOTAL PLANNINGS === -->
    <section class="w-full my-8 flex flex-wrap justify-between items-start">
      <!-- CALENDAR  -->
      <div class="w-64 mx-auto sm:m-0 space-y-4">
        <v-calendar
          is-inline
          @dayclick="goToCalendar"
          :attributes="attrs"
          mode="multiple"
          title-position="left"
          @update:from-page="fetchCalendarTasks($event)"
          v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])"
        />
        <ButtonRedirect route="calendar" v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])">
          {{ $t("lang.msg.openCalendar") }}
        </ButtonRedirect>
      </div>
      <!-- TOTAL PLANNINGS  -->
      <div class="w-64 mx-auto mt-8 sm:m-0 text-center space-y-4">
        <div class="bg-white text-primario py-4 rounded-sm">
          <span>Total visualizado</span>
          <span class="text-4xl ml-2">{{ totalPlannings }}</span>
        </div>
        <ButtonAction @action="printPlanningOnPdf" large>
          {{ $t("lang.msg.printList") }}
        </ButtonAction>
      </div>
    </section>

    <TheModal :open="isModalOpen" title="Descatalogar Producción" @close="isModalOpen = false">
      <template #body>
        <p class="mt-4 pr-16 text-gray-700 font-semibold">
          {{ $t('lang.msg.discontinueProduction') }}
        </p>
        <textarea
          rows="2"
          class="form-textarea w-full mt-2"
          placeholder="Escriba el motivo aquí"
          maxlength="256"
          v-model="current.cancellationReason"
        />
          <span>{{current.cancellationReason? current.cancellationReason.length : 0}}/256</span>
      </template>
      <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 hover:bg-red-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
            @click="discontinuePlanning"
          >{{ $t('lang.msg.discontinue') }}</button>
        </span>
      </template>
    </TheModal>

    <!-- ======================== -->
    <!-- === SLIDE OVER PANEL === -->
    <!-- ======================== -->
    <SlideOver :open="isSlideOpen" @close="closeSlide">
      <Notifications
        :extra-params="extraParams"
        :notifications="planningNotifications"
        @note-added="noteAdded"
        @client-changed="notificationsClientChanged"
      />
    </SlideOver>
  </div>
</template>

<script>
// jsPDF
import jsPDF from "jspdf";
import "jspdf-autotable";

// MIXINS
import PlanningMixin from "@/components/mixins/PlanningMixin";
import TasksMixin from "@/components/mixins/TasksMixin";

import PlanningTableItem from "@/components/shared/planning/PlanningTableItem";
import PlanningPagination from "@/components/shared/planning/PlanningPagination";

import TheModal from "@/components/shared/TheModal";
import SlideOver from "@/components/shared/SlideOver";
import Notifications from "@/components/Notifications";
import draggable from 'vuedraggable'

export default {
  name: "PlanningProduction",
  components: {
    PlanningTableItem,
    PlanningPagination,
    TheModal,
    SlideOver,
    Notifications,
      draggable
  },
  mixins: [PlanningMixin, TasksMixin],
  data() {
    return {
      homeFilter: 0,
      isModalOpen: false,
      onPageTransit: false,
      // SLIDE OVER
      isSlideOpen: false,
      planningNotifications: {},
      extraParams: {
        sample: {}
      },
      showClientNotifications: true
    };
  },
    computed:{
        isSmallScreen(){
            return window.innerWidth < 1280
        }
    },
  created() {
    // If the user comes from the 'Home' component.
      if (this.$route.params.homeFilter!==undefined){
          this.homeFilter = this.$route.params.homeFilter;
      }

    // Pagination and Total records for different filters.
    if (this.homeFilter == 1) {
      this.title = this.$t("lang.msg.productionsDelivered");
      this.setPaginationUrls(
        "api/Productions/PaginationCompletadas",
        "api/Productions/TotalRegistrosCompletadas"
      );
    }else if(this.homeFilter == 2){
        this.title = this.$t("lang.msg.productionsDiscontinued");
        this.setPaginationUrls(
            "api/Productions/PaginationDescatalogo",
            "api/Productions/TotalRegistrosDescatalogada"
        );

    } else {
      this.title = this.$t("lang.msg.productionPlanning");
      this.setPaginationUrls(
        "api/Productions/Pagination",
        "api/Productions/TotalRegistros"
      );
    }

    this.fetchPaginationData();
    this.fetchCalendarTasks();
  },
  methods: {
      changeFilter(v){
          this.isFiltering=v!==undefined
      },
      setUpDiscontinued(){
          this.homeFilter=2
          this.getDiscontinuedPlannings(`api/Productions/PaginationDescatalogo`, `api/Productions/TotalRegistrosDescatalogada`)
      },
      filterAction(){
          if (this.homeFilter == 2) {
              this.filterPlannings('api/Productions/DiscontinuedGetBy2', 'api/Productions/DiscontinuedTotalRegistrosGetBy')
          }else{
              this.filterPlannings('api/Productions/PaginationGetBy', 'api/Productions/TotalRegistrosGetBy')
          }
      },
    viewPlanning(index) {
      this.$router.push({
        name: "production",
        params: {
          idProduction: this.planning[index].idProduction
        }
      });
    },
    openDiscontinueModal(index, idProduction) {
      this.isModalOpen = true;
      this.current.index = index;
      this.current.idProduction = idProduction;
    },
    discontinuePlanning() {
      this.$axios
        .post("api/Productions/Descatalogo", {
          idUser: this.user.id,
          idProduction: this.current.idProduction,
          discontinuedProductionReason: this.current.cancellationReason
        })
        .then(() => {
          this.planning.splice(this.current.index, 1);
          this.current = {};
          this.isModalOpen = false;
          this.openAlert("success", "Producción descatalogada", null);
        })
        .catch(() => this.showFriendlyError());
    },
    // === SLIDE OVER FUNCTIONS. ===
    openNotificationsModal(sample) {
      this.showClientNotifications = this.hasRoles(['FABRICA']) ? false : true;

      this.initExtraParams(sample);

      this.fetchSampleComments(sample, this.showClientNotifications);
    },
    initExtraParams(sample) {
      // Object with extra parameters that will be used on 'Notifications' component.
      if (sample != null) {
        this.extraParams = {
          sample: sample,
          referencia:
            sample.comercialRequestReference + sample.comercialRequestReferenceNumber,
          idFactory: sample.idFactory,
          cliente: this.showClientNotifications
        };
      } else {
        this.extraParams = {
          sample: {},
          referencia: "",
          idFactory: 0,
          cliente: this.showClientNotifications
        };
      }
    },
    fetchSampleComments(sample, showClientNotifications) {
      this.$axios
        .post(`api/Notificaciones/GetComentario`, {
          rol: this.user.rol,
          idSample: sample.idSample,
          idUser: this.user.id,
          cliente: showClientNotifications
        })
        .then((response) => {
          this.currentSample = sample;
          this.planningNotifications = response.data;
          this.isSlideOpen = true;
        });
    },
    notificationsClientChanged({ sample, cliente }) {
      // Client is undefined after closing the SlideOver
      if (cliente !== undefined) {
        this.fetchSampleComments(sample, cliente);
      }
    },
    closeSlide() {
      this.showClientNotifications = true;
      this.isSlideOpen = false;

      this.initExtraParams(null);
    },
    noteAdded(item) {
      this.planningNotifications.unshift(item);
    },
    printPlanningOnPdf() {
      // Only pt supported (not mm or in)
      var doc = new jsPDF("l", "pt");


        const head = [['',
            'Referencia 2',
            'Referencia cliente',
            'Comercial interno',
            'Comercial cliente',
            'Cliente',
            'Dpto.',
            'Color',
            'Composición',
            'Artículo',
            'Fábrica',
            'Origen','Colección','Cantidades totales pedido','Precio de compra','Precio de venta','Incoterm','Fecha entrega','JL','REO','REP']]
        const body = [[]];
        let planningClone = [...this.planning ];
        planningClone.sort((a,b)=>{
            return  (b.employeeName+"").localeCompare(a.employeeName)  ||
                    (b.factoryName+"").localeCompare(a.factoryName) ||
                    new Date(b.fechaLlegadaReal) - new Date(a.fechaLlegadaReal);
        })
        planningClone.forEach((element,index) => {
            element.productionRepetition = element.comercialRequestReferenceNumber.includes('R');
            element.order = index+1;
        })
        planningClone.forEach((element,index) => {
            body[index].push(index+1);
            body[index].push(element.comercialRequestReference2);
            body[index].push(element.comercialRequestClientReference);
            body[index].push(element.employeeName);
            body[index].push(element.buyerName);
            body[index].push(element.clientName);
            body[index].push(element.sectionName);
            body[index].push(element.baseColorEs);
            body[index].push(element.compositionEs);
            body[index].push(element.subfamilyArticleEs);
            body[index].push(element.factoryName);
            body[index].push(element.factoryLocation);
            body[index].push(element.collectionEs);
            body[index].push(element.precioCompra);
            body[index].push(element.salePrice);
            body[index].push(element.icoterm);
            body[index].push(element.fechaLlegadaReal);
            body[index].push(element.joinLife);
            body[index].push(element.productionReoperation);
            body[index].push(element.productionRepetition);
            body.push([]);
        })
        doc.autoTable({
            head:head,
            body:body,
            foot:[['Total: '+planningClone.length]],
            styles: { fontSize: 6 },
            theme: "striped",
            margin: { top: 40 },
            didParseCell: function (data) {
                if (data.row.section === 'body' && data.column.dataKey === 'joinLife') {
                    data.cell.styles.fillColor = [198, 246, 213]; // Join Life
                    if (data.cell.text == "true") {
                        data.cell.styles.fillColor = [198, 246, 213]; // Join Life
                    }
                    data.cell.text = "";
                }

                if (data.row.section === 'body' && data.column.dataKey === 'productionReoperation') {
                    if (data.cell.text == "true") {
                        data.cell.styles.fillColor = [254, 235, 200]; // Reoperated
                    }
                    data.cell.text = "";
                }

                if (data.row.section === 'body' && data.column.dataKey === 'productionRepetition') {
                    if (data.cell.text == "true") {
                        data.cell.styles.fillColor = [190, 227, 248]; // Repetition
                    }
                    data.cell.text = "";
                }
            }
        });



      let currentDate = this.$moment().format("DDMMYYYY HHmmss");

      // Print task in PDF.
      doc.save(`PlanningProducción_${currentDate}.pdf`);
    }
  },
  watch: {
    planning() {
      this.onPageTransit = !this.onPageTransit;
    }
  }
};
</script>
<style lang="css">
.border-red{
    border: 2px solid #ffc4c4;
}
</style>
